/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

function removeNextDiv() {
  var swatchColorElements = document.querySelectorAll(
    ".collection-swatch-color",
  );
  swatchColorElements.forEach(function (swatchColorElement) {
    var nextElement = swatchColorElement.nextElementSibling;
    while (nextElement) {
      if (nextElement.tagName.toLowerCase() === "div") {
        nextElement.style.display = "none";
        break;
      }
      nextElement = nextElement.nextElementSibling;
    }
  });
}
function observeAndRemoveDivs() {
  var observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      if (mutation.type === "childList") {
        removeNextDiv();
      }
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

document.addEventListener("DOMContentLoaded", function () {
  removeNextDiv();
  observeAndRemoveDivs();
});

document.addEventListener("DOMContentLoaded", () => {
  const productBlocks = Array.from(document.querySelectorAll(".product-block"));
  productBlocks.forEach((block) => {
    const container = block.querySelector(".product-price");
    if (!container) return;
    const hasFakeSwatch = Boolean(container.querySelector(".swatch-colors"));
    if (hasFakeSwatch) return;
    const optionsList = container.querySelector(
      ".product-block-options__inner",
    );
    if (!optionsList || !optionsList.firstElementChild) return;
    optionsList.firstElementChild.classList.add("active-swatch");

    optionsList.addEventListener("click", (e) => {
      const item = e.target.closest(".product-block-options__item");
      if (!item) return;

      const currentActive = optionsList.querySelector(".active-swatch");
      if (currentActive) currentActive.classList.remove("active-swatch");

      item.classList.add("active-swatch");
    });
  });
});

// pdp mobile no-header display

(function () {
  if (window.location.href.indexOf("products") === -1) return;
  if (window.innerWidth >= 769) return;
  const header = document.querySelector(".section-header");
  if (!header) return;
  window.addEventListener(
    "scroll",
    function () {
      if (window.scrollY !== 0) {
        header.style.height = "55px";
        header.style.transform = "translateY(0)";
      } else {
        header.style.height = "0";
        header.style.transform = "translateY(-55px)";
      }
    },
    { passive: true },
  );
})();

// cart drawer upsell

document.addEventListener("change", (e) => {
  if (!e.target.matches(".cart-item-upsells .cart-upsell-select")) return;

  const selectedOptions = [];
  const optionsElement = e.target.closest(".cart-upsell-options");
  const currentForm = e.target.closest(
    ".js-product-form.product-purchase-form",
  );

  if (!currentForm) return;

  const variants = JSON.parse(
    currentForm.querySelector(".product-variants-dictionary").textContent,
  );
  const inputHidden = currentForm.querySelector(".hidden-variant-id-input");
  const addToCartButton = currentForm.querySelector(".add-to-cart");

  optionsElement.querySelectorAll(".cart-upsell-select").forEach((select) => {
    selectedOptions.push(select.value);
  });

  const matchingVariant = findMatchingVariant(variants, selectedOptions);

  if (matchingVariant) {
    inputHidden.value = matchingVariant.id;
    addToCartButton.disabled = !matchingVariant.available;
    addToCartButton.textContent = matchingVariant.available
      ? addToCartButton.dataset.addToCartText
      : "Sold out";
  } else {
    addToCartButton.textContent = "Unavailable";
    addToCartButton.disabled = true;
  }
});
function findMatchingVariant(variantsArray, selectedOptionsArray) {
  let variant;
  variantsArray.forEach((v) => {
    if (
      JSON.stringify([...v.options].sort()) ===
      JSON.stringify([...selectedOptionsArray].sort())
    ) {
      variant = v;
    }
  });
  return variant;
}
document.addEventListener("on:cart:after-merge", (e) => {
  const cartDrawer = document.querySelector(".cart-drawer__content");
  if (cartDrawer) {
    cartDrawer.scrollTo({ top: 0, behavior: "smooth" });
  }
});

function initBundles() {
  const cartContexts = document.querySelectorAll("cart-form");

  cartContexts.forEach((cartContext) => {
    const bundleElements = Array.from(
      cartContext.querySelectorAll("[data-bundle]"),
    ).filter((el) => !el.closest(".merge-remove-item"));

    const bundleNames = [
      ...new Set(
        bundleElements
          .map((el) => el.dataset.bundle)
          .filter((name) => name !== ""),
      ),
    ];

    bundleNames.forEach((bundleName) => {
      const items = bundleElements.filter(
        (el) => el.dataset.bundle === bundleName,
      );
      updateBundleMessage(items);
    });
  });
}

function updateBundleMessage(items) {
  if (items.length === 0) return;

  const [bundleName, requiredQty, discount] =
    items[0].dataset.bundle.split("|");
  const bundleQuantity = Number(requiredQty);

  let quantity = 0;
  items.forEach((el) => {
    const input = el.querySelector(".js-quantity");
    if (input) {
      quantity += Number(input.value);
    }
  });

  console.log(
    "Bundle:",
    bundleName,
    "| qty:",
    quantity,
    "| required:",
    bundleQuantity,
  );

  const message = items[items.length - 1].querySelector(".message");
  if (message) {
    if (quantity < bundleQuantity) {
      const remaining = bundleQuantity - quantity;
      message.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path><line x1="7" y1="7" x2="7" y2="7"></line></svg> <span>Add ${remaining} more ${bundleName} and save ${discount}%</span>`;
    } else {
      message.innerHTML = "";
    }
  }
}

// Initialize on page load
document.addEventListener("DOMContentLoaded", () => {
  initBundles();
});

// Re-initialize after cart DOM updates
document.addEventListener("on:cart:after-merge", () => {
  initBundles();
});
